import type { ReactNode } from "react";

/**
 * Per-tab section header for the Coach Workspace list pages.
 *
 * Ports the legacy `admin-panel-head` / `roster-head` pattern
 * (`index.html`): each coach sub-tab card leads with a
 * DESCRIPTIVE title (`<h3>`) + a sub-line explaining the tab,
 * with the page's action buttons on the right.
 *
 * This is distinct from — and sits below — the shell's constant
 * "Coaching Workspace" header. The tab name is never repeated:
 * the strip already shows which tab is active; this header's job
 * is to describe it.
 */

export type PageHeaderProps = {
  /** Descriptive title — e.g. "Review Playlists", not "Playlists". */
  title: string;
  /** One-line explanation of what this tab is for. */
  description: ReactNode;
  /** Optional action buttons, right-aligned on the same row. */
  actions?: ReactNode;
};

export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <div
      className="grid min-w-0 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start"
      data-testid="coach-page-header"
    >
      <div className="min-w-0">
        <h2 className="text-lg font-display text-ink">{title}</h2>
        <p className="mt-0.5 max-w-3xl text-sm leading-snug text-ink-muted">
          {description}
        </p>
      </div>
      {actions ? (
        <div
          className="-mx-1 overflow-x-auto px-1 pb-1 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0"
          data-testid="coach-page-header-actions"
        >
          <div className="flex w-max items-center gap-2 [&>*]:shrink-0 sm:w-auto sm:flex-wrap sm:justify-end">
            {actions}
          </div>
        </div>
      ) : null}
    </div>
  );
}
